import { ApolloProvider } from '@apollo/client';
import { Button, Typography } from '@mui/material';
import { SearchkitClient, SearchkitProvider } from '@searchkit/client';
import React, { MutableRefObject, useCallback, useState } from 'react';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { FEATURE_CONSTANTS } from '../common/constants/featureConstants';
import { PREFERENCE_ENTITY_CONTEXTS_CONSTANT } from '../common/constants/preferenceConstants';
import I18n from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { H, V } from '../common/Layout';
import DataList from '../common/list/DataList';
import ListView from '../common/list/ListView';
import { searchClient, slimResultsQuery } from '../common/list/slimQuery';
import { useStyling } from '../common/Theme';
import { PersonProvider } from '../person/PersonContext';
import ShortlistGuestEditorModal from '../person/ShortlistGuestEditorModal';
import usePersonUri from '../person/usePersonUri';
import { MarketplaceProfileDetails } from './MarketplaceProfileDetails';

const List = ({
    id,
    data,
    feature,
    isInSidePanel = false,
    onAddToShortlist,
    shortlistLineItems,
    setIsEditorOpen,
    handleItemClick,
    ...rest
}) => {
    const ItemRenderer = useCallback(
        ({ item, ...rendererProps }) => (
            <MarketplaceProfileDetails
                item={item}
                shortlistLineItems={shortlistLineItems}
                onAddToShortlist={onAddToShortlist}
                onSeeMore={() => handleItemClick(item)}
                {...rendererProps}
            />
        ),
        [handleItemClick, onAddToShortlist, shortlistLineItems]
    );
    return (
        <DataList
            id={id}
            feature={feature}
            border
            divider
            items={data?.results?.hits?.items}
            onClick={() => {}}
            listItemSx={{
                '&:hover': {
                    backgroundColor: 'transparent',
                    cursor: 'auto'
                }
            }}
            ItemRenderer={ItemRenderer}
            pagination={data?.results.hits.page}
            disableRipple={isInSidePanel}
            emptyListElement={
                <V sx={{ alignItems: 'center', gap: 1 }}>
                    <Typography>
                        <I18n token="error.emptyResult" />
                    </Typography>
                    <Button
                        onClick={() => {
                            setIsEditorOpen(true);
                        }}
                    >
                        <I18n token="form.person.create" />
                    </Button>
                </V>
            }
            {...rest}
        />
    );
};

export default function PersonMarketplaceList({
    isInSidePanel = false,
    immutableFilters = undefined,
    onAddToShortlist = undefined,
    shortlistLineItems,
    id = undefined,
    handleItemClick,
    orgRole = undefined,
    entity = ENTITY_CONSTANTS.person
}: {
    isInSidePanel?: boolean;
    immutableFilters?: { identifier: string; value?: string }[] | undefined;
    onAddToShortlist?: (item: any) => void;
    shortlistLineItems?: MutableRefObject<string[]>;
    id?: string;
    orgRole?: string;
    handleItemClick?: (item?: any) => void;
    entity?: string;
}) {
    const searchkit = new SearchkitClient();
    const { isMobile } = useStyling();
    const uri = usePersonUri();
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const EntityListRenderer = useCallback(
        (rendererProps) => (
            <List
                onAddToShortlist={onAddToShortlist}
                isInSidePanel={isInSidePanel}
                shortlistLineItems={shortlistLineItems}
                handleItemClick={handleItemClick}
                setIsEditorOpen={setIsEditorOpen}
                {...rendererProps}
            />
        ),
        [handleItemClick, isInSidePanel, onAddToShortlist, shortlistLineItems]
    );
    return (
        <ApolloProvider client={searchClient}>
            <SearchkitProvider client={searchkit}>
                <PersonProvider>
                    <ListView
                        id={id}
                        feature={FEATURE_CONSTANTS.peopleResources}
                        entity={entity}
                        preferenceEntityKey={PREFERENCE_ENTITY_CONTEXTS_CONSTANT.marketplace}
                        uri={uri}
                        quickFilters={[
                            {
                                identifier: 'isSharedInDirectory',
                                label: (
                                    <H sx={{ mt: '2px' }}>
                                        <Icons.ShowrunnrFilter />
                                    </H>
                                ),
                                tooltip: <I18n token="marketplace.tooltip" />
                            }
                        ]}
                        query={slimResultsQuery('Person', true, 'SharePersonInDirectory')}
                        EntityListRenderer={EntityListRenderer}
                        saveFiltersDisabled
                        enableSearch
                        EntityTableRenderer={undefined}
                        immutableFiltersWithSameIdentifier={immutableFilters}
                        enableImport={false}
                        ImportView={undefined}
                        defaultView={'List'}
                        showBreadCrumbs={false}
                        containerSx={{ height: '100%', pr: isMobile ? 1 : 2 }}
                    />
                    {isEditorOpen ? (
                        <ShortlistGuestEditorModal
                            id={id}
                            onClose={() => {
                                setIsEditorOpen(false);
                            }}
                            setIsOpen={() => {
                                setIsEditorOpen(false);
                            }}
                            isOpen={isEditorOpen}
                            orgRole={orgRole}
                            onAddToShortlist={onAddToShortlist}
                            shortlistLineItems={shortlistLineItems}
                        />
                    ) : (
                        <></>
                    )}
                </PersonProvider>
            </SearchkitProvider>
        </ApolloProvider>
    );
}
